import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { employeesAPI, locationsAPI, importExportAPI, branchesAPI, companyAPI } from '../lib/api';
import { computeOrgUnitCounts } from '../components/OrgUnitFilter';
import { formatLastFirst, getDuplicateNameKeys } from '../lib/nameFormat';
import { useOrgLabels } from '../contexts/OrgLabelsContext';
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Eye,
  Users,
  Filter,
  Upload,
  Download,
  FileSpreadsheet,
  Loader2,
  CheckCircle,
  AlertCircle,
  FileDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MapPin,
  AlertTriangle,
  History,
} from 'lucide-react';
import { toast } from 'sonner';
import { FilterPanel } from './employees/FilterPanel';import { FilterChips } from './employees/FilterChips';
import { useEmployeeFilters } from './employees/useEmployeeFilters';
import { AZFilterStrip, normalizeFirstLetter } from './employees/AZFilterStrip';
import { DepartmentBadges } from '../components/DepartmentBadges';
import { PositionBadges } from './employees/PositionBadges';
import { ConfirmDeleteDialog } from '../components/ui/confirm-delete-dialog';
import { XlsxImportDialog } from '../components/XlsxImportDialog';
import { EmployeeImportHistoryDialog } from '../components/EmployeeImportHistoryDialog';
import { DuplicateCleanupDialog } from '../components/DuplicateCleanupDialog';

export default function Employees() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgL = useOrgLabels();
  const fileInputRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [orgTree, setOrgTree] = useState([]);
  const [orgLevels, setOrgLevels] = useState([]);
  const [loading, setLoading] = useState(true);

  // Reichhaltiger Filter-Zustand. Default: nur aktive Mitarbeiter.
  // Vorbelegung per URL (?departments=BvB) — z. B. Klick aus der Personalanalyse.
  const [filters, setFilters] = useState({
    search: '',
    org_unit: searchParams.get('org_unit') || 'all',
    status: 'active',
    temporary: 'all',
    taetigkeitsart: 'all',
    onboarding: 'all',
    departments: (searchParams.get('departments') || '').split(',').map((s) => s.trim()).filter(Boolean),
    positions: [],
    employment_types: [],
    massnahmen: [],
    city: '',
    birth_months: [],
    birthday_window: null,
    az_letter: null,
    qualifications: [],
    trainings: [],
  });
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  // Sortierung — Default: alphabetisch nach Nachname.
  const [sortKey, setSortKey] = useState('last_name');
  const [sortDir, setSortDir] = useState('asc');
  
  // Import/Export states
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [updateImportOpen, setUpdateImportOpen] = useState(false);
  const [importHistoryOpen, setImportHistoryOpen] = useState(false);
  const [dupCleanupOpen, setDupCleanupOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Datei aus dem Vorschau-Schritt — wird beim Bestätigen erneut hochgeladen
  const [pendingImportFile, setPendingImportFile] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const patchFilters = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const resetFilters = () => setFilters({
    search: '',
    org_unit: 'all',
    status: 'active',
    temporary: 'all',
    taetigkeitsart: 'all',
    onboarding: 'all',
    departments: [],
    positions: [],
    employment_types: [],
    massnahmen: [],
    city: '',
    birth_months: [],
    birthday_window: null,
    az_letter: null,
    qualifications: [],
    trainings: [],
  });

  // Anzahl aktiver Filter (fuer Badge im "Filter"-Button) — Default 'active' zaehlt nicht
  const activeFilterCount = [
    filters.org_unit !== 'all',
    filters.status !== 'all' && filters.status !== 'active',
    filters.temporary !== 'all',
    filters.onboarding !== 'all',
    filters.az_letter !== null,
    !!filters.city,
    !!filters.birthday_window,
    (filters.departments || []).length > 0,
    (filters.positions || []).length > 0,
    (filters.employment_types || []).length > 0,
    (filters.massnahmen || []).length > 0,
    (filters.birth_months || []).length > 0,
    (filters.qualifications || []).length > 0,
    (filters.trainings || []).length > 0,
  ].filter(Boolean).length;

  const empMassnahmenMap = null;

  // Mitarbeiterzahl je Org-Einheit (inkl. Untereinheiten) für den Baum-Filter.
  const orgUnitCounts = useMemo(
    () => computeOrgUnitCounts(orgTree, employees.filter((e) => e.is_active)),
    [orgTree, employees]
  );

  const filteredEmployees = useEmployeeFilters({
    employees, filters, empMassnahmenMap, sortKey, sortDir, orgTree,
  });
  const empDupKeys = getDuplicateNameKeys(employees);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ k }) => (
    sortKey !== k
      ? <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />
      : sortDir === 'asc'
        ? <ArrowUp className="h-3 w-3 ml-1" />
        : <ArrowDown className="h-3 w-3 ml-1" />
  );

  const loadData = async () => {
    try {
      const [empRes, locRes, branchRes, treeRes] = await Promise.all([
        employeesAPI.getAll(),
        locationsAPI.getAll(),
        branchesAPI.getAll(),
        companyAPI.getOrgTree().catch(() => ({ data: { tree: [], levels: [] } })),
      ]);
      setEmployees(empRes.data);
      setLocations(locRes.data);
      setBranches(branchRes.data);
      setOrgTree(treeRes.data?.tree || []);
      setOrgLevels(treeRes.data?.levels || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const [employeeToDelete, setEmployeeToDelete] = useState(null);

  const handleDelete = async (id) => {
    try {
      await employeesAPI.delete(id);
      setEmployees(employees.filter((e) => e.id !== id));
      toast.success('Mitarbeiter gelöscht');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // Download import template
  const handleDownloadTemplate = async () => {
    try {
      const response = await importExportAPI.getTemplate();
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mitarbeiter_import_vorlage.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Vorlage heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen der Vorlage');
    }
  };

  // Handle file import — Schritt 1: Dry-Run-Vorschau, Schritt 2: tatsächlicher Import nach Bestätigung
  const handleFileImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Nur XLSX/XLS Dateien erlaubt');
      return;
    }

    setImporting(true);
    setImportResult(null);
    setPendingImportFile(null);

    try {
      // Schritt 1: Vorschau (kein DB-Schreiben)
      const previewResp = await importExportAPI.importEmployees(file, true);
      setImportResult(previewResp.data);
      setPendingImportFile(file);
      setImportDialogOpen(true);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Fehler beim Erstellen der Vorschau';
      toast.error(detail);
      setImportResult({ success: false, error: detail });
      setImportDialogOpen(true);
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Schritt 2: Tatsächlichen Import nach Bestätigung ausführen
  const confirmImport = async () => {
    if (!pendingImportFile) return;
    setImporting(true);
    try {
      const response = await importExportAPI.importEmployees(pendingImportFile, false);
      setImportResult(response.data);
      setPendingImportFile(null);
      if (response.data.imported_count > 0) {
        toast.success(`${response.data.imported_count} Mitarbeiter importiert`);
        loadData();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Fehler beim Import';
      toast.error(detail);
      setImportResult({ success: false, error: detail });
    } finally {
      setImporting(false);
    }
  };

  const cancelImport = () => {
    setImportDialogOpen(false);
    setImportResult(null);
    setPendingImportFile(null);
  };

  // Export employees
  const handleExport = async (branchId = null, format = 'xlsx', orgUnitId = null) => {
    setExporting(true);
    try {
      const response = await importExportAPI.exportEmployees(branchId, format, orgUnitId);
      const ext = format === 'csv' ? 'csv' : 'xlsx';
      const mime = format === 'csv'
        ? 'text/csv;charset=utf-8'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mitarbeiter_export_${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export erfolgreich');
    } catch (error) {
      toast.error('Fehler beim Export');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-10 w-32" />
          </div>
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="employees-page">
        {/* Hidden file input for import */}
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept=".xlsx,.xls"
          onChange={handleFileImport}
          data-testid="import-file-input"
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">Mitarbeiter</h1>
            <p className="text-muted-foreground mt-1">{employees.length} Mitarbeiter insgesamt</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Import/Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="import-export-btn">
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Import/Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleDownloadTemplate} data-testid="download-template-btn">
                  <FileDown className="mr-2 h-4 w-4" />
                  Vorlage herunterladen
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => fileInputRef.current?.click()} 
                  disabled={importing}
                  data-testid="import-btn"
                >
                  {importing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  XLSX importieren
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setUpdateImportOpen(true)}
                  data-testid="update-import-btn"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Stammdaten aktualisieren (XLSX)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setImportHistoryOpen(true)}
                  data-testid="import-history-btn"
                >
                  <History className="mr-2 h-4 w-4" />
                  Import-Verlauf / Rückgängig
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDupCleanupOpen(true)}
                  data-testid="duplicate-cleanup-btn"
                >
                  <Users className="mr-2 h-4 w-4" />
                  Duplikate bereinigen
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => handleExport(null, 'xlsx')} 
                  disabled={exporting}
                  data-testid="export-all-btn"
                >
                  {exporting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  Alle als Excel (.xlsx) exportieren
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleExport(null, 'csv')} 
                  disabled={exporting}
                  data-testid="export-all-csv-btn"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Alle als CSV (.csv) exportieren
                </DropdownMenuItem>
                {filters.org_unit && filters.org_unit !== 'all' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleExport(null, 'xlsx', filters.org_unit)}
                      disabled={exporting}
                      data-testid="export-filtered-btn"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Gefilterte Auswahl exportieren (.xlsx)
                    </DropdownMenuItem>
                  </>
                )}
                {branches.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    {branches.map((branch) => (
                      <DropdownMenuItem 
                        key={branch.id}
                        onClick={() => handleExport(branch.id)}
                        disabled={exporting}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        {branch.name} exportieren
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/employees/new">
              <Button className="rounded-full" data-testid="add-employee-btn">
                <Plus className="mr-2 h-4 w-4" />
                Neuer Mitarbeiter
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-border/60">
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Suchen nach Name, E-Mail, Personalnummer, Stadt, Position... (Tippfehler-tolerant)"
                  value={filters.search}
                  onChange={(e) => patchFilters({ search: e.target.value })}
                  className="pl-10 h-10"
                  data-testid="employee-search-input"
                />
              </div>

              <FilterPanel
                open={filterPanelOpen}
                onOpenChange={setFilterPanelOpen}
                filters={filters}
                onPatch={patchFilters}
                onReset={resetFilters}
                employees={employees}
                locations={locations}
                branches={branches}
                orgTree={orgTree}
                orgLevels={orgLevels}
                orgUnitCounts={orgUnitCounts}
                massnahmen={[]}
                activeCount={activeFilterCount}
              />

              <Select value={`${sortKey}:${sortDir}`} onValueChange={(v) => {
                const [k, d] = v.split(':');
                setSortKey(k); setSortDir(d);
              }}>
                <SelectTrigger className="w-full sm:w-[210px] h-10" data-testid="sort-select">
                  <ArrowUpDown className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Sortierung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_name:asc">Nachname A → Z</SelectItem>
                  <SelectItem value="last_name:desc">Nachname Z → A</SelectItem>
                  <SelectItem value="first_name:asc">Vorname A → Z</SelectItem>
                  <SelectItem value="position:asc">Einsatz als A → Z</SelectItem>
                  <SelectItem value="position:desc">Einsatz als Z → A</SelectItem>
                  <SelectItem value="birthday:asc">Geburtstag (nächster zuerst)</SelectItem>
                  <SelectItem value="employment_start:desc">Eintritt (neueste zuerst)</SelectItem>
                  <SelectItem value="employment_start:asc">Eintritt (älteste zuerst)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <FilterChips
              filters={filters}
              locations={locations}
              branches={branches}
              orgTree={orgTree}
              orgLevels={orgLevels}
              massnahmen={[]}
              onPatch={patchFilters}
              onReset={resetFilters}
            />

            <AZFilterStrip
              employees={employees.filter((e) => filters.status !== 'active' || e.is_active)}
              selected={filters.az_letter}
              onSelect={(letter) => patchFilters({ az_letter: letter })}
            />

            {/* Tätigkeitsart-Umschalter */}
            <div className="inline-flex rounded-full border border-border/60 bg-secondary/30 p-0.5" data-testid="taetigkeitsart-filter">
              {[
                { v: 'all', label: 'Alle' },
                { v: 'operativ', label: 'Operativ' },
                { v: 'verwaltung', label: 'Verwaltung' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => patchFilters({ taetigkeitsart: opt.v })}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    (filters.taetigkeitsart || 'all') === opt.v
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`taetigkeitsart-filter-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <p className="text-xs text-muted-foreground" data-testid="result-count">
              {filteredEmployees.length} von {employees.length} Mitarbeitern angezeigt
            </p>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-border/60">
          <CardContent className="p-0">
            {filteredEmployees.length > 0 ? (
              <>
              {/* Desktop/Tablet: Tabelle ab md */}
              <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow className="data-table">
                    <TableHead>
                      <button
                        type="button"
                        className="flex items-center font-semibold hover:text-foreground"
                        onClick={() => toggleSort('last_name')}
                        data-testid="sort-by-name"
                      >
                        Name <SortIcon k="last_name" />
                      </button>
                    </TableHead>
                    <TableHead className="hidden md:table-cell">
                      <button
                        type="button"
                        className="flex items-center font-semibold hover:text-foreground"
                        onClick={() => toggleSort('position')}
                        data-testid="sort-by-position"
                      >
                        Einsatz als <SortIcon k="position" />
                      </button>
                    </TableHead>
                    <TableHead className="hidden md:table-cell">Maßnahme/Abteilung</TableHead>
                    <TableHead className="hidden lg:table-cell">{orgL.location}</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map((employee, index) => (
                    <TableRow
                      key={employee.id}
                      className="animate-slide-up cursor-pointer hover:bg-secondary/20"
                      style={{ animationDelay: `${index * 0.02}s` }}
                      onClick={() => navigate(`/employees/${employee.id}`)}
                      data-testid={`employee-row-${employee.id}`}
                    >
                      <TableCell>
                        <div className="font-medium flex items-center gap-2">
                          {formatLastFirst(employee, empDupKeys)}
                          {employee.onboarding_status === 'anwaerter' && (
                            <Badge
                              variant="outline"
                              className="text-xs border-amber-400 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30"
                              data-testid={`employee-anwaerter-${employee.id}`}
                            >
                              Anwärter
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <PositionBadges employee={employee} testIdPrefix={`employee-${employee.id}`} />
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <DepartmentBadges
                          text={employee.department}
                          testIdPrefix={`employee-dept-${employee.id}`}
                        />
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="space-y-1">
                          {employee.location_id ? (
                            <span>{employee.location_name || '-'}</span>
                          ) : employee.is_active ? (
                            <Badge
                              variant="outline"
                              className="text-xs gap-1 border-amber-400 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30"
                              title="Ohne Standort werden Feiertage/Bundesland falsch berechnet (Fallback auf Firmen-Standard). Bitte Standort zuweisen."
                              data-testid={`employee-missing-location-${employee.id}`}
                            >
                              <AlertTriangle className="h-3 w-3" />
                              Kein {orgL.location}
                            </Badge>
                          ) : (
                            <span>-</span>
                          )}
                          {employee.additional_location_names && employee.additional_location_names.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {employee.additional_location_names.map((name) => (
                                <Badge key={name} variant="outline" className="text-xs">
                                  +{name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" data-testid={`employee-actions-${employee.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/employees/${employee.id}`); }}>
                              <Eye className="mr-2 h-4 w-4" />
                              Anzeigen
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/employees/${employee.id}/edit`); }}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Bearbeiten
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={(e) => { e.stopPropagation(); setEmployeeToDelete(employee); }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Löschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>

              {/* Mobil: kompakte Karten-Ansicht (kein horizontales Scrollen) */}
              <div className="md:hidden divide-y divide-border/60" data-testid="employees-mobile-list">
                {filteredEmployees.map((employee, index) => (
                  <div
                    key={employee.id}
                    className="flex items-start gap-3 p-4 active:bg-secondary/30 cursor-pointer animate-slide-up"
                    style={{ animationDelay: `${index * 0.02}s` }}
                    onClick={() => navigate(`/employees/${employee.id}`)}
                    data-testid={`employee-card-${employee.id}`}
                  >
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <p className="font-semibold text-sm truncate">
                        {formatLastFirst(employee, empDupKeys)}
                      </p>
                      <PositionBadges employee={employee} testIdPrefix={`employee-m-${employee.id}`} />
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        {employee.department && (
                          <span className="truncate">{employee.department}</span>
                        )}
                        {employee.location_name && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3 flex-shrink-0" />
                            {employee.location_name}
                          </span>
                        )}
                        {!employee.location_id && employee.is_active && (
                          <span
                            className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium"
                            data-testid={`employee-missing-location-m-${employee.id}`}
                          >
                            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                            Kein {orgL.location}
                          </span>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="flex-shrink-0 -mt-1" data-testid={`employee-actions-m-${employee.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/employees/${employee.id}`); }}>
                          <Eye className="mr-2 h-4 w-4" />
                          Anzeigen
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/employees/${employee.id}/edit`); }}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Bearbeiten
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={(e) => { e.stopPropagation(); setEmployeeToDelete(employee); }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
              </div>
              </>
            ) : (
              <div className="text-center py-12">
                <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-medium text-lg">Keine Mitarbeiter gefunden</h3>
                <p className="text-muted-foreground mt-1">
                  {(filters.search || activeFilterCount > 0)
                    ? 'Versuchen Sie andere Filteroptionen oder setzen Sie alle Filter zurück'
                    : 'Erstellen Sie den ersten Mitarbeiter'}
                </p>
                {!filters.search && activeFilterCount === 0 ? (
                  <Link to="/employees/new">
                    <Button className="mt-4 rounded-full">
                      <Plus className="mr-2 h-4 w-4" />
                      Mitarbeiter anlegen
                    </Button>
                  </Link>
                ) : (
                  <Button variant="outline" className="mt-4 rounded-full" onClick={resetFilters}>
                    Alle Filter zurücksetzen
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Import Vorschau / Ergebnis Dialog */}
        <XlsxImportDialog
          open={updateImportOpen}
          onOpenChange={setUpdateImportOpen}
          title="Mitarbeiter-Stammdaten aktualisieren"
          description="Excel hochladen → Vorschau prüfen → bestätigen. Nur bestehende Mitarbeiter werden aktualisiert."
          infoLines={[
            'Abgleich über Vorname + Nachname. Die E-Mail dient nur zur Unterscheidung bei Namensgleichheit.',
            'Standardmäßig werden nur bestehende Mitarbeiter aktualisiert. Optional können neue angelegt werden.',
            'Nicht eindeutig zuordenbare Personen werden übersprungen.',
            'Beim Update überschreiben nur ausgefüllte Zellen; leere bleiben unverändert.',
          ]}
          templateUrl="/employees/import/template"
          previewUrl="/employees/import/preview"
          commitUrl="/employees/import"
          templateFilename="Vorlage_Mitarbeiter.xlsx"
          enableFieldSelection
          enableCreateNew
          undoUrlBase="/employees/import/batches"
          onImported={loadData}
        />

        <EmployeeImportHistoryDialog
          open={importHistoryOpen}
          onOpenChange={setImportHistoryOpen}
          onChanged={loadData}
        />

        <DuplicateCleanupDialog
          open={dupCleanupOpen}
          onOpenChange={setDupCleanupOpen}
          onChanged={loadData}
        />

        <Dialog open={importDialogOpen} onOpenChange={(o) => { if (!o) cancelImport(); else setImportDialogOpen(true); }}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {importResult?.success === false ? (
                  <>
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    Import fehlgeschlagen
                  </>
                ) : importResult?.dry_run ? (
                  <>
                    <Eye className="h-5 w-5 text-blue-500" />
                    Vorschau — bitte prüfen
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    Import abgeschlossen
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                {importResult?.error ? (
                  importResult.error
                ) : importResult?.dry_run ? (
                  <>
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      {importResult.imported_count} Mitarbeiter werden angelegt
                    </span>
                    {importResult.error_count > 0 && (
                      <span className="text-orange-600 dark:text-orange-400">
                        {' • '}{importResult.error_count} Zeilen mit Fehlern werden übersprungen
                      </span>
                    )}
                    . Es wurde noch nichts gespeichert.
                  </>
                ) : importResult ? (
                  <>
                    {importResult.imported_count} Mitarbeiter erfolgreich importiert.
                    {importResult.error_count > 0 && (
                      <span className="text-orange-500">
                        {' '}{importResult.error_count} Fehler aufgetreten.
                      </span>
                    )}
                  </>
                ) : null}
              </DialogDescription>
            </DialogHeader>

            {/* Fehler-Liste */}
            {importResult?.errors?.length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-2">
                <p className="text-sm font-medium text-muted-foreground">
                  {importResult?.dry_run ? 'Diese Zeilen werden NICHT importiert:' : 'Fehler:'}
                </p>
                {importResult.errors.map((err, index) => (
                  <div
                    key={`err-row-${err.row || index}`}
                    className="text-sm p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800"
                    data-testid={`import-error-row-${err.row || index}`}
                  >
                    <span className="font-medium">Zeile {err.row}:</span> {err.error}
                  </div>
                ))}
              </div>
            )}

            {/* Vorschau-Tabelle (oder Erfolgsliste) */}
            {importResult?.imported?.length > 0 && (
              <div className="max-h-72 overflow-y-auto">
                <p className="text-sm font-medium text-muted-foreground mb-2">
                  {importResult?.dry_run ? 'Diese Mitarbeiter werden angelegt:' : 'Importiert:'}
                </p>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2 font-medium">Zeile</th>
                        <th className="text-left p-2 font-medium">Personal-Nr.</th>
                        <th className="text-left p-2 font-medium">Name</th>
                        <th className="text-left p-2 font-medium">Einsatz als</th>
                        <th className="text-left p-2 font-medium">Maßnahme/Abteilung</th>
                        <th className="text-left p-2 font-medium">{orgL.location}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.imported.map((entry, index) => (
                        <tr
                          key={entry.employee_number || `imp-${index}`}
                          className={`border-t hover:bg-muted/30 ${entry.warning ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}
                          data-testid={`import-preview-row-${entry.row || index}`}
                        >
                          <td className="p-2 text-muted-foreground">{entry.row}</td>
                          <td className="p-2 font-mono text-xs">{entry.employee_number}</td>
                          <td className="p-2 font-medium">{entry.name}</td>
                          <td className="p-2">{entry.position || '-'}</td>
                          <td className="p-2">
                            <DepartmentBadges text={entry.department} />
                          </td>
                          <td className="p-2 text-xs">
                            {entry.warning ? (
                              <span className="text-amber-700 dark:text-amber-300" title={entry.warning}>
                                ⚠ ohne {orgL.location}
                              </span>
                            ) : entry.location_id ? (
                              <span className="text-emerald-700 dark:text-emerald-400">✓ gesetzt</span>
                            ) : (
                              <span className="text-muted-foreground">–</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <DialogFooter>
              {importResult?.dry_run && pendingImportFile ? (
                <>
                  <Button
                    variant="outline"
                    onClick={cancelImport}
                    disabled={importing}
                    data-testid="cancel-import-dialog"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    onClick={confirmImport}
                    disabled={importing || !importResult?.imported_count}
                    data-testid="confirm-import-dialog"
                  >
                    {importing
                      ? 'Importiere...'
                      : `${importResult.imported_count} Mitarbeiter jetzt importieren`}
                  </Button>
                </>
              ) : (
                <Button onClick={cancelImport} data-testid="close-import-dialog">
                  Schließen
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmDeleteDialog
          open={!!employeeToDelete}
          onOpenChange={(o) => !o && setEmployeeToDelete(null)}
          title="Mitarbeiter löschen?"
          description={
            employeeToDelete
              ? `Möchten Sie ${employeeToDelete.last_name}, ${employeeToDelete.first_name} (Personalnummer ${employeeToDelete.employee_number || '—'}) wirklich endgültig löschen? Alle hinterlegten Dokumente, Weiterbildungen und Qualifikationen gehen verloren. Diese Aktion wird in den Zugriffsprotokollen vermerkt.`
              : ''
          }
          onConfirm={() => {
            if (employeeToDelete) handleDelete(employeeToDelete.id);
            setEmployeeToDelete(null);
          }}
          testIdPrefix="confirm-delete-employee"
        />
      </div>
    </DashboardLayout>
  );
}
